<script lang="ts">
  import { onMount } from "svelte"
  import Progress from "./Progress.svelte"
  import EditGoalStatus from "$app/Pages/Plans/Goals/EditGoalStatus.svelte"
  import Form from "$app/Pages/Plans/Goals/Progress/Form.svelte"
  import Task from "./Task.svelte"
  import _ from "lodash"
  import { formatDate } from "$app/utils"
  import { slide } from "svelte/transition"
  import { Plus, Minus, MessageSquareText } from "lucide-svelte"
  import { appStore } from "$app/store.svelte"

  let props = $props()
  let {
    pos
  }: {
    pos: number
  } = props

  let goal: GoalType = $state(props.goal)

  let tasks_expanded = $state(false)
  onMount(async () => {
    if (goal.tasks?.length > 0) {
      goal.tasks.forEach(function (task) {
        if (task.progresses?.length > 0) {
          tasks_expanded = true
        }
      })
    }
  })

  let newProgress = $state(false)

  let assignee = null
  switch (goal.assignee_type) {
    case "User":
      assignee = appStore.users[goal.assignee_id]
      break
    case "Department":
      assignee = appStore.departments[goal.assignee_id]
      break
  }

  function withinDates(date: string): boolean {
    const d = new Date(date)
    return (
      appStore.dateStart.getTime() <= d.getTime() &&
      d.getTime() <= appStore.dateEnd.getTime()
    )
  }
</script>

<div
  class="bg-white border-l-4 relative shadow"
  class:border-quarterly-goal={goal.type === "QuarterlyGoal"}
  class:border-annual-goal={goal.type !== "QuarterlyGoal"}
>
  <div
    class="grid grid-cols-8 gap-1 text-sm border-b border-t border-gray-200 py-2 px-4"
  >
    <div class="col-span-5 lg:col-span-6 flex items-center relative">
      <span class="text-gray-400 text-xs mr-2">{pos}.</span>
      <h3 class="font-semibold text-base lg:text-lg text-gray-800">
        {goal.name}
      </h3>
      {#if withinDates(goal.created_at)}
        <div
          class="absolute top-1 right-1 rounded px-1 text-xs bg-red-500 text-white font-semibold"
        >
          NEW
        </div>
      {:else if goal.status === "complete"}
        <div
          class="hidden lg:inline absolute top-1 right-2 rounded px-1 text-xs bg-blue-600 text-white font-semibold"
        >
          Completed {formatDate(goal.status_updated_at)}
        </div>
      {/if}
    </div>

    <div class="col-span-3 lg:col-span-2 flex justify-end items-center">
      {#if !newProgress}
        <button
          class="mr-6"
          class:text-red-500={goal.progresses.length === 0}
          class:text-blue-500={goal.progresses.length > 0}
          onclick={() => {
            newProgress = true
          }}
        >
          <MessageSquareText class="size-6" />
        </button>
      {/if}
      {#if appStore.update_goals}
        <EditGoalStatus
          goalOrTask={goal}
          type="Goal"
          editable={appStore.update_goals}
          small={true}
          show_border={false}
          onupdate={(detail) => {
            goal.status_updated_at = detail.status_updated_at
            goal.status = detail.status
          }}
        />
      {:else}
        <div
          class="px-2 py-2 flex justify-start border-gray-200 rounded shadow-sm border text-sm items-center"
        >
          <div
            class={`rounded-full w-3 h-3 inline-block capitalize bg-${goal.status}`}
          ></div>
          <div
            class="ml-2 flex flex-row items-center justify-start whitespace-nowrap capitalize"
          >
            {goal.status.replace(/_/g, " ")}
          </div>
        </div>
      {/if}
    </div>
  </div>

  {#if goal.tasks?.length || goal.progresses?.length || newProgress}
    <div class="py-2 px-4">
      {#if newProgress}
        <div transition:slide class="mb-4">
          <Form
            progress={{ plan_id: appStore.plan.id, [`goal_id`]: goal.id }}
            type="goal"
            operation="create"
            oncreate={(detail) => {
              goal.progresses = [detail, ...goal.progresses]
              newProgress = false
            }}
            oncancel={() => {
              newProgress = false
            }}
          />
        </div>
      {:else}
        {#each goal.progresses as progress}
          <Progress
            {progress}
            clazz="shadow-md bg-green-100 bg-opacity-25 mb-3"
          />
        {/each}
      {/if}

      {#if goal.tasks?.length}
        <div
          class="flex justify-between items-center hover:cursor-pointer py-1 px-3"
          class:bg-task={tasks_expanded}
          class:bg-blue-100={!tasks_expanded}
          class:text-white={tasks_expanded}
          class:text-task={!tasks_expanded}
          role="button"
          tabindex="0"
          onclick={() => {
            tasks_expanded = !tasks_expanded
          }}
          onkeydown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              tasks_expanded = !tasks_expanded
            }
          }}
        >
          <h3 class="font-semibold">Tasks</h3>
          {#if tasks_expanded}
            <Minus class="w-5 h-5 mr-1" />
          {:else}
            <Plus class="w-5 h-5 mr-1" />
          {/if}
        </div>
        {#if tasks_expanded}
          <div
            transition:slide
            class="border-l-2 border-task px-4 py-2 shadow-lg"
          >
            {#each _.sortBy(goal.tasks, "parent_position") as task, idx (task.id)}
              <Task {task} pos={idx + 1} />
            {/each}
          </div>
        {/if}
      {/if}
    </div>
  {/if}
</div>
